import { LoginRequest } from '@/data/@types/LoginRequest'
import { SearchMode, initialSearchMode } from '@/data/@types/SearchMode'
import { getData, postData, deleteData } from '@/helpers/util/webApiUtil'
import { store } from '..'
import { MUTATIONS } from '../mutations-types'

const querystring = require('querystring') // eslint-disable-line

const BASE_URL = process.env.VUE_APP_API_BASE_URL

const errorMessage = '入力された認証情報が不正です。入力内容を見直してください。'

enum LoginType {
  SSO = 'sso',
  BUILTIN = 'builtin'
}

enum Location {
  NDL = 'ndl',
  KSS = 'kss',
  REKION = 'rekion',
  DAISY = 'daisy',
  SHINSAI = 'sinsai',
  SENDLIBRARIES = 'sendlibraries',
  UNKNOWN = 'unknown'
}

const state = {
  LoginType: '',
  patron: {},
  loggedInUser: {},
  location: 'unknown',
  isLoggedIn: false,
  hasError: false,
  errorText: ''
}

const deleteLoggedinData = async () => {
  store.commit('SET_AUTH_STATE', false)
  store.commit('SET_PATRON', {})
  store.commit('MY_COLLECTION_CLEAR')
  store.commit('CLEAR_LOGGEDIN_USER')
  store.commit('CLEAR_LOGIN_TYPE')
}

const _isLoggedIn = (state: any): boolean => {
  return state.isLoggedIn
}

const _isRegistered = (state: any): boolean => {
  const patron = state.patron
  const type = patron?.type
  return type === 'REGISTERED'
}

const _isSimple = (state: any): boolean => {
  const patron = state.patron
  const registrationStatus = patron?.registrationStatus
  return registrationStatus === 'SIMPLE'
}

const _isApplying = (state: any): boolean => {
  //  利用者登録状態が簡易登録で、本人確認書類確認状態コード[CertificateStatusCode]が確認中[INPROCESS]、保留[PENDING]のいずれか
  const patron = state.patron
  const registrationStatus = patron?.registrationStatus
  const certificateStatusCode = patron?.certificate?.status
  return registrationStatus === 'SIMPLE' && (certificateStatusCode === 'INPROCESS' || certificateStatusCode === 'PENDING')
}

const _isPersendUserType = (state: any): boolean => {
  //  国会議員、国会職員、NDL職員、登録利用者か確認
  const allowType = ['DIET_MEMBER', 'DIET_STAFF', 'REGISTERED', 'NDL_STAFF']
  const patron = state.patron
  return patron?.type && allowType.includes(patron?.type)
}

const _isOfficialPersendUserType = (state: any): boolean => {
  //  国会議員、国会職員、NDL職員か確認
  const allowType = ['DIET_MEMBER', 'DIET_STAFF', 'NDL_STAFF']
  const patron = state.patron
  return patron?.type && allowType.includes(patron?.type)
}

const _isPersendUserCountry = (state: any): boolean => {
  //  Patronの利用者種別[type]が登録利用者[REGISTERED]の場合、居住国[country]の値が「JP」であること。
  const allowType = ['DIET_MEMBER', 'DIET_STAFF', 'NDL_STAFF']
  const patron = state.patron
  const type = patron?.type
  const country = patron?.country
  if (allowType.includes(type)) return true
  return _isRegistered(state) && country === 'JP'
}

const _isPersendAgreed = (state: any): boolean => {
  const patron = state.patron
  return patron?.attrs && patron.attrs.find((attr: any) => attr.key === 'PERSEND' && attr.value === 'ACCEPTED')
}

const _isPersendForbidden = (state: any): boolean => {
  const patron = state.patron
  return patron?.restrictions && patron.restrictions.find((restriction: any) => restriction.type === 'PERSEND' || restriction.type === 'ALL')
}

const _isPersendPerfect = (state: any) => {
  //  ログインしていない場合
  if (!_isLoggedIn(state)) return false
  //  国会議員、国会職員、NDL職員、登録利用者でない場合
  if (!_isPersendUserType(state)) return false
  //  「利用者種別」が＜登録利用者＞の場合は「登録状態」が＜本登録＞かつ「居住国」が＜日本＞であること。
  if (!_isPersendUserCountry(state)) return false
  //  「個人送信サービス同意状態」が＜同意＞であること。
  if (!_isPersendAgreed(state)) return false
  //  「利用制限」で、＜個人送信利用禁止＞または＜全サービス利用禁止＞がかかっていないこと。
  if (_isPersendForbidden(state)) return false
  return true
}

const getters = {
  isLoggedIn (state: any) {
    return _isLoggedIn(state)
  },
  // 個人送信or図書館送信アカウント判定
  isPersendOrLibsend (state: any) {
    const patron = state.patron
    if (patron?.attrs && patron.attrs.find((attr: any) => attr.key === 'LIBSEND')) {
      // 図書館送信
      return true
    } else if (_isPersendPerfect(state)) {
      // 個人送信-手続き済み
      return true
    } else {
      // 個人送信-未手続き
      return false
    }
  },
  //  登録利用者の場合
  isRegistered (state: any): boolean {
    return _isRegistered(state)
  },
  //  簡易登録ユーザーの場合
  isSimple (state: any): boolean {
    return _isSimple(state)
  },
  //  本登録申請中の場合
  isApplying (state: any): boolean {
    return _isApplying(state)
  },
  //  個人送信の条件をすべて満たしているか判定
  isPersendPerfect (state: any): boolean {
    return _isPersendPerfect(state)
  },
  //  個人送信対象のユーザー種別か判定
  isPersendUserType (state: any): boolean {
    return _isPersendUserType(state)
  },
  //  国会議員、国会職員、NDL職員か判定
  isOfficialPersendUserType (state: any): boolean {
    return _isOfficialPersendUserType(state)
  },
  //  個人送信対象ユーザーの居住国が対応しているか判定
  isPersendUserCountry (state: any): boolean {
    return _isPersendUserCountry(state)
  },
  //  「個人送信サービス同意状態」が＜同意＞であること。
  isPersendAgreed (state: any): boolean {
    return _isPersendAgreed(state)
  },
  //  「利用制限」で、＜個人送信利用禁止＞または＜全サービス利用禁止＞がかかっていないこと。
  isPersendForbidden (state: any): boolean {
    return _isPersendForbidden(state)
  },
  // 個人送信アカウント判定
  isPersend (state: any) {
    const patron = state.patron
    if (patron?.attrs && patron.attrs.find((attr: any) => attr.key === 'PERSEND')) {
      return true
    } else {
      return false
    }
  },
  // 個人送信アカウント判定（手続き途中であるかの判断が必要な場合）
  isMiddlePersend (state: any) {
    const patron = state.patron
    if (patron?.attrs && patron.attrs.find((attr: any) => attr.key === 'PERSEND' && attr.value !== 'ACCEPTED')) {
      // 個人送信-手続き途中
      return false
    } else {
      // 個人送信-手続き済み、個人送信-未手続き
      return true
    }
  },
  // 図書館送信アカウント判定
  isSendlibraries (state: any) {
    const patron = state.patron
    return patron?.attrs && !!patron.attrs.find((attr: any) => attr.key === 'LIBSEND')
  },
  // 図書館送信参加館アカウント判定
  isLibsend (state: any) {
    const patron = state.patron
    if (!patron?.attrs) return ''
    const libsend = patron.attrs.filter((attr: any) => attr.key === 'LIBSEND')
    return libsend[0]?.value || ''
  },
  // 統計ダウンロード可能判定
  canDownloadStatistics (state: any): boolean {
    const patron = state.patron
    if (!patron?.attrs) return false
    const libsend = patron.attrs.find((attr: any) => ['LIBSEND_B_STAFF', 'LIBSEND_BP_STAFF'].includes(attr.value))
    return libsend !== undefined
  },
  // れきおん参加館アカウント判定
  isRekionBuilding (state: any) {
    const loginUserRole = state.loggedInUser.role || []
    return loginUserRole.includes('REKION')
  },
  // れきおん参加館管理者アカウント判定
  isRekionAdmin (state: any) {
    const loginUserRole = state.loggedInUser.role || []
    return loginUserRole.includes('REKION_ADMIN')
  },
  libraryName (state: any) {
    const patron = state.patron
    return patron?.name || ''
  },
  loginType (state: any) {
    return state.LoginType
  },
  loggedInUser (state: any) {
    return state.loggedInUser
  },
  location (state: any) {
    return state.location
  },
  hasErrorInLoginPage (state: any) {
    return state.hasError
  },
  errorTextInLoginPage (state: any) {
    return state.errorText
  },
  searchMode () {
    const cookieSearchMode = <any>[]
    const isServer = typeof document === 'undefined'
    if (!isServer) {
      document.cookie.split(';').map(cookie => {
        if (cookie.split('=')[0].includes('searchMode')) {
          cookieSearchMode.push(JSON.parse(cookie.split('=')[1]))
        }
      })
      return cookieSearchMode[0] || initialSearchMode
    }
  },
  patron (state: any) {
    return state.patron
  }
}

const mutations = {
  LOGIN (state: any) {
    state.isLoggedIn = true
    state.LoginType = LoginType.SSO
  },
  LOGOUT (state: any) {
    state.isLoggedIn = false
  },
  BUILTIN_LOGIN (state: any, user: any) {
    state.isLoggedIn = true
    state.LoginType = LoginType.BUILTIN
    state.loggedInUser = user
  },
  CLEAR_LOGGEDIN_USER (state: any) {
    state.loggedInUser = {}
  },
  CLEAR_LOGIN_TYPE (state: any) {
    state.LoginType = ''
  },
  HAS_ERROR (state: any, hasError: boolean) {
    state.hasError = hasError
  },
  SET_PATRON (state: any, patron: any) {
    state.patron = patron
  },
  SET_ERROR_TEXT (state: any, errorText: string) {
    state.errorText = errorText
  },
  SET_AUTH_STATE (state: any, isLoggedIn: boolean) {
    state.isLoggedIn = isLoggedIn
  },
  SET_LOCATON (state: any, location: Location) {
    state.location = location
  }
}

const actions = {
  async login (context: any, request: LoginRequest) {
    await postData(`${BASE_URL}/auth/sso/login`, request).then((response: any) => {
      if (response.status === 401) {
        console.error('response.ok:', response.ok)
        console.error('response.statusText:', response.statusText)
        // const json = await response.json()
        throw new Error('入力された認証情報が不正です。入力内容を見直してください。')
      }
      context.commit('SET_PATRON', response.patron)
      context.commit('HAS_ERROR', false)
      context.commit('SET_ERROR_TEXT', '')
      context.commit('LOGIN')
    }).catch((error: Error) => {
      // ネットワークエラーでも !response.ok でもここで処理できる
      console.log(error.message)
      context.commit('HAS_ERROR', true)
      context.commit('SET_ERROR_TEXT', errorMessage)
    })
  },
  async logout (context: any) {
    try {
      await context.commit('LOGOUT')
      const url = `${BASE_URL}/auth/sso/logout`
      await deleteData(url)
      // 画面が再描画されないので明示的にログアウト状態を設定
      deleteLoggedinData()
    } catch (e) {
      console.log(e)
      await context.commit('LOGOUT')
      // セッションタイムアウト（404）のとき、フロントログアウトを手動で実行
      deleteLoggedinData()
    }
  },
  async builtinLogin (context: any, request: FormData) {
    await postData(`${BASE_URL}/auth/builtin/login`, request).then((response: any) => {
      if (response.status === 401) {
        console.error('response.ok:', response.ok)
        console.error('response.statusText:', response.statusText)
        // const json = await response.json()
        throw new Error('入力された認証情報が不正です。入力内容を見直してください。')
      }
      context.commit('HAS_ERROR', false)
      context.commit('SET_ERROR_TEXT', '')
      context.commit('BUILTIN_LOGIN', response)
    }).catch((error: Error) => {
      // ネットワークエラーでも !response.ok でもここで処理できる
      console.log(error.message)
      context.commit('HAS_ERROR', true)
      context.commit('SET_ERROR_TEXT', errorMessage)
    })
  },
  async builtinLogout (context: any) {
    const url = `${BASE_URL}/auth/builtin/logout`
    await postData(url)

    // 画面が再描画されないので明示的にログアウト状態を設定
    deleteLoggedinData()
  },
  async getAuthState (context: any) {
    const url = `${BASE_URL}/auth/state`
    const authState = await getData(url).then((response: any) => {
      const isLoggedIn = store.getters.isLoggedIn
      // 別タブでログアウトしている場合、ログイン情報を削除
      if (isLoggedIn && response.cardId === 'anonymousUser') deleteLoggedinData()

      // ログインしていない場合、ログアウト直後の場合、アカウント情報をセットせずリターン
      if (response.cardId === 'anonymousUser' || !response.authenticated) return response

      if (response.patron) {
        // 通常ログインしている場合、アカウントの情報を再セット
        context.commit('SET_PATRON', response.patron)
        context.commit('LOGIN')
      } else {
        // 組み込みログインしている場合、組み込みアカウントの情報を再セット
        context.commit('BUILTIN_LOGIN', {
          cardId: response.cardId,
          role: response.authorities
        })
      }
      return response
    })
    return authState
  },
  async setLocation (context: any) {
    await getData(`${BASE_URL}/geography`).then((response: any) => {
      context.commit('SET_LOCATON', response.value)
    })
  },
  async setSearchMode (context: any, searchMode: SearchMode) {
    const isServer = typeof document === 'undefined'
    if (!isServer) {
      const now = new Date()
      now.setFullYear(now.getFullYear() + 1)
      document.cookie = `searchMode=${JSON.stringify(searchMode)};expires=${now.toUTCString()};`
    }
  },
  async sessionCheck (context: any) {
    // ログインしていない場合はセッションチェックをしない
    const isLoggedIn = store.getters.isLoggedIn
    if (!isLoggedIn) return true

    const isPersend = store.getters.isPersend
    const isSendlibraries = store.getters.isSendlibraries
    const isRekionBuilding = store.getters.isRekionBuilding
    const authState = await store.dispatch('getAuthState')

    // 個人送信、図書館送信、れきおん参加館のアカウントはログアウト
    if (!authState.authenticated && (isPersend || isSendlibraries)) {
      await store.dispatch('logout')
      store.commit(MUTATIONS.SESSION_TIMEOUT_NOTIFICATION)
      return false
    } else if (authState.cardId === 'anonymousUser' && isRekionBuilding) {
      await store.dispatch('builtinLogout')
      store.commit(MUTATIONS.SESSION_TIMEOUT_NOTIFICATION)
      return false
    }
    return true
  }

}

export default {
  state,
  actions,
  mutations,
  getters
}
